import React, { useState } from 'react';

import { IconButton } from '@mui/material';
import { useAccessGuard } from 'src/components/auth';
import type { AlineButtonProps } from 'src/components/generic';
import { AlineButton, AlineTooltip } from 'src/components/generic';
import { IconColors } from 'src/components/icons/icon-utils';
import { NormalArrowDownIcon } from 'src/components/icons/NormalArrowDownIcon';
import { Apollo } from 'src/gql/generated';
import { tooltipMsg } from 'src/helpers';
import { useCheckWorkflowAuthorization } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { AddWorkflowFilesMenu } from './AddWorkflowFilesMenu';
import { AddIcon } from '../icons/AddIcon';

const useStyles = makeStyles()(theme => ({
  icon: {
    marginLeft: theme.spacing(1),
  },
  iconButton: {
    padding: '2px',
  },
  menuIcon: {
    height: 24,
    marginRight: theme.spacing(1),
    width: 24,
  },
  uploadButton: {
    borderRadius: 24,
    height: 32,
    padding: theme.spacing(1, 2),
  },
}));

interface AddWorkflowFilesButtonProps {
  workflowId: UUID;
  buttonVariant?: AlineButtonProps['variant'] | 'icon';
  className?: string;
  dataCy?: string;
  dense?: boolean;
  stroke?: string;
  uploadItemDataCy?: string;
  uploadPickerDataCy?: string;
}

export function AddWorkflowFilesButton({
  buttonVariant = 'contained',
  className,
  dataCy,
  dense,
  stroke = IconColors.White,
  uploadItemDataCy,
  uploadPickerDataCy,
  workflowId,
}: AddWorkflowFilesButtonProps): JSX.Element | null {
  const { classes } = useStyles();
  const [selectMenuOpen, setSelectMenuOpen] = useState<boolean>(false);
  const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null);
  const { canEditWorkflow, workflow } = useCheckWorkflowAuthorization(workflowId);
  const handleToggleMenu = useAccessGuard({
    callback: () => setSelectMenuOpen(open => !open),
    minRole: Apollo.OrgRole.Basic,
  });
  const disabledReason = !canEditWorkflow ? tooltipMsg.notWFEditor : '';

  return (
    <>
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div className={className}>
          {buttonVariant === 'icon' ? (
            <IconButton
              ref={setButtonEl}
              aria-label="Add documents to workflow"
              className={classes.iconButton}
              color="primary"
              size="small"
              sx={{ color: theme => theme.appColors.iconsType }}
              onClick={handleToggleMenu}
            >
              <AddIcon />
            </IconButton>
          ) : (
            <AlineButton
              ref={setButtonEl}
              buttonClassName={classes.uploadButton}
              color="primary"
              data-cy={dataCy}
              disabled={Boolean(disabledReason)}
              variant={buttonVariant}
              onClick={handleToggleMenu}
            >
              Add document
              <NormalArrowDownIcon className={classes.icon} stroke={stroke} />
            </AlineButton>
          )}
        </div>
      </AlineTooltip>
      <AddWorkflowFilesMenu
        anchorEl={buttonEl}
        dense={dense}
        open={selectMenuOpen}
        uploadItemDataCy={uploadItemDataCy}
        uploadPickerDataCy={uploadPickerDataCy}
        workflow={workflow}
        workflowId={workflowId}
        onClose={() => setSelectMenuOpen(false)}
      />
    </>
  );
}
